import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaMoneyBillWave, FaMobileAlt } from 'react-icons/fa';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px 40px;
  box-sizing: border-box; 
  overflow-x: hidden;
  
  @media (max-width: 480px) {
    padding: 0 8px 30px;
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
    padding: 20px 0;
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    letter-spacing: 2px;
    padding: 15px 0;
    margin: 20px 0 15px;
  }
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
    
    @media (max-width: 768px) {
      width: 80%;
      max-width: 280px;
    }
    
    @media (max-width: 480px) {
      width: 90%;
      max-width: 240px;
    }
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-top: 8px;
  }
`;

const RegistryCard = styled(motion.div)`
  background-color: #fcfaf7;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin: 20px 0;
  position: relative;
  width: 100%;
  max-width: 600px;
  text-align: center;
  box-sizing: border-box; // Add this to include padding in width calculation
  
  @media (max-width: 768px) {
    padding: 25px 15px;
    margin: 15px 0;
    width: 95%;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
    margin: 10px 0;
    border-radius: 6px;
    width: 100%; // Change to 100% to fill container
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, #d4b59e, #947060);
  }
`;

const RegistryText = styled.p`
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin: 15px 0;
    padding: 0 5px;
  }
`;

const GiftOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px 0;
  width: 100%;
  box-sizing: border-box; // Add this
  
  @media (max-width: 768px) {
    margin: 25px 0;
  }
  
  @media (max-width: 480px) {
    margin: 20px 0 15px;
    padding: 0 5px; // Add padding on mobile
  }
`;

const OptionsSectionTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.6rem;
  margin-bottom: 25px;
  font-weight: 500;
  position: relative;
  padding-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 18px;
    padding-bottom: 8px;
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 100px;
    
    @media (max-width: 480px) {
      width: 80px;
    }
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  width: 100%;
  margin-top: 15px;
  box-sizing: border-box; // Add this
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    width: 100%; // Change to 100%
  }
`;

const GiftOptionCard = styled.div`
  background-color: white;
  padding: 25px 20px;
  border-radius: 8px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box; // Add this
  
  /* Cash gift card styling */
  ${props => props.cash && `
    width: 100%;
    max-width: 530px;
    
    @media (max-width: 600px) {
      width: 100%;
      max-width: none;
    }
  `}
  
  /* Venmo cards styling */
  ${props => props.venmo && `
    width: calc(50% - 15px);
    max-width: 240px;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 12px rgba(0, 0, 0, 0.1);
    }
    
    @media (max-width: 600px) {
      width: 100%;
      max-width: none;
    }
  `}
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
  }
`;

const OptionTitle = styled.h3`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.3rem;
    
    @media (max-width: 480px) {
      font-size: 1.2rem;
    }
  }
`;

const OptionText = styled.p`
  font-family: 'Lato', sans-serif;
  color: #666;
  font-size: 1rem;
  line-height: 1.5;
  word-wrap: break-word;
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const VenmoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-top: 18px;
  }
  
  @media (max-width: 480px) {
    margin-top: 15px;
  }
`;

const VenmoProfileImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #d4b59e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    width: 110px;
    height: 110px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
    border: 2px solid #d4b59e;
    margin-bottom: 10px;
  }
`;

const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VenmoUsername = styled.p`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 10px 0 5px;
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin: 8px 0 4px;
  }
`;

const VenmoNote = styled.p`
  font-family: 'Lato', sans-serif;
  color: #888;
  font-size: 0.9rem;
  margin: 5px 0;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 3px 0;
  }
`;

const ThankYouText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.4rem;
  font-style: italic;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-top: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-top: 20px;
  }
`;

const Registry = () => {
  return (
    <PageContainer>
      <PageTitle>
        Gift Registry
        <NameSpan>Erika & Andrew</NameSpan>
      </PageTitle>
      
      <RegistryCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <RegistryText>
          Your presence at our wedding is the greatest gift of all. However, if you wish to honor us with a gift, we would appreciate a cash gift to help us build our new life together.
        </RegistryText>
        
        <GiftOptionsContainer>
          <OptionsSectionTitle>Gift Options</OptionsSectionTitle>
          
          <OptionsContainer>
            {/* Cash gift option - full width */}
            <GiftOptionCard cash>
              <OptionTitle>
                <FaMoneyBillWave /> Cash Gift
              </OptionTitle>
              <OptionText>
                Cash gifts will be gratefully accepted at our reception. A card box will be available at the venue.
              </OptionText>
            </GiftOptionCard>
          </OptionsContainer>
          
          {/* Venmo section title */}
          <OptionsSectionTitle style={{ marginTop: '30px', fontSize: '1.4rem' }}>
            Digital Gift Options
          </OptionsSectionTitle>
          
          {/* Venmo options - side by side on desktop, stacked on mobile */}
          <OptionsContainer style={{ flexDirection: 'row' }}>
            <GiftOptionCard venmo>
              <OptionTitle>
                <FaMobileAlt /> Erika's Venmo
              </OptionTitle>
              <OptionText>
                Send a gift using Venmo to Erika's account.
              </OptionText>
              
              <VenmoContainer>
                <VenmoProfileImage>
                  <ProfileImg src="/img/erika-yoon-venmo.jpg" alt="Erika's Venmo" />
                </VenmoProfileImage>
                <VenmoUsername>@Erika-Yoon</VenmoUsername>
                <VenmoNote>Search for username</VenmoNote>
              </VenmoContainer>
            </GiftOptionCard>

            <GiftOptionCard venmo>
              <OptionTitle>
                <FaMobileAlt /> Andrew's Venmo
              </OptionTitle>
              <OptionText>
                Send a gift using Venmo to Andrew's account.
              </OptionText>
              
              <VenmoContainer>
                <VenmoProfileImage>
                  <ProfileImg src="/img/andrew-wang-venmo.jpg" alt="Andrew's Venmo" />
                </VenmoProfileImage>
                <VenmoUsername>@shamudrew</VenmoUsername>
                <VenmoNote>Search for username</VenmoNote>
              </VenmoContainer>
            </GiftOptionCard>
          </OptionsContainer>
        </GiftOptionsContainer>
        
        <ThankYouText>Thank you for your love and support!</ThankYouText>
      </RegistryCard>
    </PageContainer>
  );
};

export default Registry;